import * as React from 'react';
import { FormControl, MenuItem, Select, Stack, CircularProgress } from '@mui/material';
import { useLocalStorage } from '../../../../../../utils/useLocalStorage';
import { useGetProvidersAndModelsQuery } from '../../../../../../services/aiAgentApi';
import { useIntl } from 'react-intl';

// Types for the API response
interface ProviderModel {
	provider: string;
	model: string;
	displayName?: string;
	description?: string;
	isActive: boolean;
}

interface ModelOption {
	value: string;
	label: string;
	description?: string;
}

interface ProviderOption {
	value: string;
	label: string;
}

interface AiProviderSelectorProps {
	onProviderChange?: (provider: string, model: string) => void;
	provider?: string; // Optional provider parameter that overrides settings
}

const AiProviderSelector: React.FC<AiProviderSelectorProps> = ({ onProviderChange, provider: forcedProvider }) => {
	const { formatMessage } = useIntl();

	const [provider, setProvider] = useLocalStorage('aiProvider', 'openai');
	const [openAiModel, setOpenAiModel] = useLocalStorage('openAIModel', 'gpt-5');
	const [geminiModel, setGeminiModel] = useLocalStorage('geminiModel', 'gemini-2.0-flash');

	// Use forced provider if provided, otherwise use the stored provider
	const effectiveProvider = forcedProvider || provider;
	const isReadOnly = !!forcedProvider;

	// Fetch providers and models from API
	const { data: providersAndModels, isLoading, error } = useGetProvidersAndModelsQuery();

	// Filter models by provider and active status
	const getModelsForProvider = (targetProvider: string): ModelOption[] => {
		if (!providersAndModels) return [];

		return (providersAndModels as ProviderModel[])
			.filter((item: ProviderModel) => item.provider === targetProvider && item.isActive)
			.map((item: ProviderModel) => ({
				value: item.model,
				label: item.displayName || item.model,
				description: item.description,
			}));
	};

	// Get available providers (unique and active)
	const getAvailableProviders = (): ProviderOption[] => {
		if (!providersAndModels) return [];

		const uniqueProviders = Array.from(
			new Set(
				(providersAndModels as ProviderModel[])
					.filter((item: ProviderModel) => item.isActive)
					.map((item: ProviderModel) => item.provider),
			),
		);

		return uniqueProviders.map((providerName: string) => ({
			value: providerName,
			label: providerName.charAt(0).toUpperCase() + providerName.slice(1),
		}));
	};

	const handleProviderChange = (event: any) => {
		// Don't allow changes if provider is forced
		if (isReadOnly) return;

		const newProvider = event.target.value;
		setProvider(newProvider);

		// Get the current model from local storage based on provider
		const currentModel = newProvider === 'openai' ? openAiModel : geminiModel;

		// Get available models for the new provider
		const availableModels = getModelsForProvider(newProvider);

		// Set to first available model if current model is not found
		if (!currentModel || !availableModels.find((m: ModelOption) => m.value === currentModel)) {
			const firstModel = availableModels[0]?.value;
			if (firstModel) {
				if (newProvider === 'openai') {
					setOpenAiModel(firstModel);
				} else {
					setGeminiModel(firstModel);
				}
				if (onProviderChange) {
					onProviderChange(newProvider, firstModel);
				}
			}
		} else {
			if (onProviderChange) {
				onProviderChange(newProvider, currentModel);
			}
		}
	};

	const handleModelChange = (event: any) => {
		const newModel = event.target.value;
		if (effectiveProvider === 'openai') {
			setOpenAiModel(newModel);
		} else {
			setGeminiModel(newModel);
		}
		if (onProviderChange) {
			onProviderChange(effectiveProvider, newModel);
		}
	};

	const availableProviders = getAvailableProviders();
	const openAiModels = getModelsForProvider('openai');
	const geminiModels = getModelsForProvider('gemini');

	// Show error state if API call fails
	if (error) {
		return (
			<Stack spacing={1} direction='row' sx={{ width: '20rem', alignItems: 'center', justifyContent: 'center' }}>
				<CircularProgress size={20} color='error' />
				<span>Error loading providers</span>
			</Stack>
		);
	}

	// Show loader while data is being fetched
	if (isLoading) {
		return (
			<Stack spacing={1} direction='row' sx={{ width: '20rem', alignItems: 'center', justifyContent: 'center' }}>
				<CircularProgress size={20} />
			</Stack>
		);
	}

	return (
		<Stack spacing={1} direction='row' sx={{ width: '20rem' }}>
			<FormControl fullWidth>
				<Select
					variant='standard'
					value={effectiveProvider}
					onChange={handleProviderChange}
					disabled={isReadOnly}
					MenuProps={{
						style: { zIndex: 99999999 },
					}}
				>
					{availableProviders.map((providerOption: ProviderOption) => (
						<MenuItem key={providerOption.value} value={providerOption.value}>
							{providerOption.label}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<FormControl fullWidth>
				<Select
					variant='standard'
					value={effectiveProvider === 'openai' ? openAiModel : geminiModel}
					onChange={handleModelChange}
					MenuProps={{
						style: { zIndex: 99999999 },
					}}
				>
					{(effectiveProvider === 'openai' ? openAiModels : geminiModels).map((model: ModelOption) => (
						<MenuItem key={model.value} value={model.value}>
							{model.label}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</Stack>
	);
};

export default AiProviderSelector;
